import { QuestionType } from "@simplyk/common";

export type Choice = {
  id?: string;
  body: string;
  sortIndex: number;
};

export type QuestionScope = "buyer" | "participant";

export type CustomPropertyOption = {
  id: string;
  body: string;
  type: QuestionType;
  choices: { id: string; body: string }[];
  scope: QuestionScope;
  scopesOnForm: QuestionScope[];
};

export const ADDRESS_OPTION_ID = "address-option";
export const GUEST_ADDRESS_OPTION_ID = "guest-address-option";
export const PHONE_NUMBER_OPTION_ID = "phone-number-option";
export const GUEST_PHONE_NUMBER_OPTION_ID = "guest-phone-number-option";
export const NOTIFICATION_PHONE_OPTION_ID = "notification-phone-option";
export const GUEST_NAME_PRESET_ID = "guest-name-preset";
export const GUEST_EMAIL_PRESET_ID = "guest-email-preset";

export interface GuestPresetOption {
  id: typeof GUEST_NAME_PRESET_ID | typeof GUEST_EMAIL_PRESET_ID;
  label: string;
  questionType: QuestionType;
}

export const COMMUNICATION_PREFERENCES_OPTION_ID = "communication-preferences-option";
export const COMMUNICATION_PREFERENCES_QUESTION_KEY = "communication-preferences";

// A buyer row backed by form configuration rather than a custom property, so the caller owns the ids.
export interface BuyerPresetOption {
  id: string;
  label: string;
  subtitle?: string;
}

export type TicketingChoiceV3 = Choice & {
  maximum: number | null;
  remainingCapacity?: number;
};

export type RateOption = {
  value: string;
  label: string;
};

export type TicketingFormQuestionRowV3 = {
  localId: string;
  questionId: string | null;
  crmCustomPropertyId: string | null;
  body: string;
  type: QuestionType;
  required: boolean;
  scope: QuestionScope;
  choices: TicketingChoiceV3[];
  sortIndex: number;
  isActiveOnForm: boolean;
  isNew: boolean;
  isDirty: boolean;
  askForAllRates: boolean;
  ratesAssigned: RateOption[];
  isMaximumActive: boolean;
  countAlreadyAnswered: number;
};

export type TicketingStepQuestionsV4FormData = {
  questions: TicketingFormQuestionRowV3[];
  askForAddress: boolean;
};

export type CreateQuestionEntryPoint = "Modal" | "Search No Results";

export type DraftQuestion = {
  id: string;
  data: CreateQuestionFormData;
};

export type AddQuestionSelectionState = {
  scope: QuestionScope;
  selectedIds: string[];
  drafts: DraftQuestion[];
};

export type TicketingModalState =
  | { type: "closed" }
  | ({ type: "add-question" } & AddQuestionSelectionState)
  | {
      type: "create-new";
      scope: QuestionScope;
      prefillBody?: string;
      entryPoint?: CreateQuestionEntryPoint;
      returnTo: AddQuestionSelectionState;
    }
  | { type: "edit"; question: TicketingFormQuestionRowV3 };

export type DonationFormQuestionRowV4 = {
  localId: string;
  questionId: string | null;
  crmCustomPropertyId: string | null;
  body: string;
  type: QuestionType;
  required: boolean;
  scope: "buyer";
  choices: Choice[];
  sortIndex: number;
  isActiveOnForm: boolean;
  isNew: boolean;
  isDirty: boolean;
};

export type DonationStepQuestionsV4FormData = {
  questions: DonationFormQuestionRowV4[];
  askForAddress: boolean;
};

export type DonationModalState =
  | { type: "closed" }
  | { type: "add-question" }
  | { type: "create-new"; prefillBody?: string; entryPoint?: CreateQuestionEntryPoint }
  | { type: "edit"; question: DonationFormQuestionRowV4 };

export type AddQuestionSelection = {
  addFullAddress: boolean;
  addGuestFullAddress: boolean;
  addPhoneNumber: boolean;
  addGuestPhoneNumber: boolean;
  addNotificationPhoneNumber: boolean;
  buyerPresetIds: string[];
  guestPresetIds: string[];
  buyerPropertyIds: string[];
  guestPropertyIds: string[];
  draftIds: string[];
};

export type CreateQuestionFormData = {
  body: string;
  type: QuestionType;
  required: boolean;
  choices: { body: string }[];
  matchedCustomProperty?: CustomPropertyOption;
  scope?: QuestionScope;
  askForAllRates?: boolean;
  ratesAssigned?: RateOption[];
  isMaximumActive?: boolean;
  choicesWithMaximum?: { body: string; maximum: number | null }[];
};
